import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Delete } from "lucide-react";
import { api } from "../lib/api.js";
import { setBearer, setCachedUser } from "../lib/auth.js";

/**
 * PIN login screen — replaces OAuth/password auth.
 * Tap digits, hit Enter. Your PIN lands you in the app.
 */
export function Login(): JSX.Element {
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const MAX = 8;

  function press(digit: string): void {
    if (pin.length >= MAX) return;
    setPin((p) => p + digit);
    setError(null);
  }

  function del(): void {
    setPin((p) => p.slice(0, -1));
    setError(null);
  }

  async function submit(): Promise<void> {
    if (!pin) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{
        token: string;
        expires_at: string;
        user: { id: string; username: string; display_name: string | null; is_admin: boolean };
      }>("/api/auth/pin", { pin });
      setBearer(res.token);
      setCachedUser(res.user);
      nav("/", { replace: true });
    } catch {
      setError("Wrong PIN. Try again.");
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ fontFamily: "'Cinzel', serif" }}>
      <div
        className="pointer-events-none fixed inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(10,4,4,0.75) 100%)" }}
      />

      <div className="relative z-10 card max-w-sm w-full p-10 flex flex-col items-center gap-8">
        <div className="text-center">
          <div className="section-label text-gold tracking-[0.4em] text-xs mb-2">WALLACE · TV</div>
          <h1 className="font-display text-ivory text-3xl" style={{ letterSpacing: "0.06em" }}>Enter PIN</h1>
          <div className="hero-underline mx-auto mt-3 w-32" />
        </div>

        {/* PIN dots */}
        <div className="flex gap-3">
          {Array.from({ length: Math.max(pin.length || 1, 4) }, (_, i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full border transition-all ${
                i < pin.length
                  ? "bg-gold border-gold"
                  : "bg-transparent border-hairline"
              }`}
            />
          ))}
        </div>

        {error && (
          <div className="text-vermilionHi text-sm text-center">{error}</div>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 w-full max-w-[240px]">
          {KEYS.map((k, i) => {
            if (k === "") return <div key={i} />;
            if (k === "⌫") {
              return (
                <button
                  key={i}
                  onClick={del}
                  disabled={busy}
                  className="flex items-center justify-center h-14 rounded-lg border border-hairline text-parchment hover:border-gold hover:text-ivory transition bg-ink/30 text-lg"
                >
                  <Delete size={18} />
                </button>
              );
            }
            return (
              <button
                key={i}
                onClick={() => press(k)}
                disabled={busy}
                className="flex items-center justify-center h-14 rounded-lg border border-hairline text-ivory hover:border-gold hover:bg-elev/40 transition bg-ink/30 text-xl font-display"
                style={{ letterSpacing: "0.05em" }}
              >
                {k}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => void submit()}
          disabled={!pin || busy}
          className="pill w-full py-3 text-base"
        >
          {busy ? "Signing in…" : "Enter"}
        </button>
      </div>
    </div>
  );
}
